/**
 * Utility for optimizing image URLs with query parameters for thumbnails.
 * Uses caching to avoid redundant string operations on re-renders.
 */

type OptimizeImageUrlOptions = {
  /** Target width/height for the optimized image (default: 400) */
  size?: number;
  /** Maximum cache size before evicting oldest entries (default: 500) */
  maxCacheSize?: number;
};

// Shared cache for optimized image URLs
const imageUrlCache = new Map<string, string>();

/**
 * Get optimal image size based on viewport (smaller on mobile for faster loading)
 */
export const getOptimalImageSize = (): number => {
  if (typeof window === 'undefined') return 400;
  const width = window.innerWidth;
  // Mobile: smaller images (saves ~50% bandwidth)
  if (width < 768) return 300;
  return 400;
};

/**
 * Optimize image URL for thumbnails (webp, minified, resized).
 * Uses caching to avoid redundant string operations on re-renders.
 *
 * @param url - The original image URL
 * @param options - Optional configuration for size and cache limits
 * @returns Optimized URL with query parameters or undefined if input is falsy
 */
export const optimizeImageUrl = (
  url: string | undefined,
  options: OptimizeImageUrlOptions = {}
): string | undefined => {
  if (!url) return url;

  const { size = 400, maxCacheSize = 500 } = options;

  // Create cache key that includes size to handle different requested sizes
  const cacheKey = `${url}:${size}`;

  // Return cached result if available
  const cached = imageUrlCache.get(cacheKey);
  if (cached) return cached;

  // Compute optimized URL
  const separator = url.includes('?') ? '&' : '?';
  const optimized = `${url}${separator}type=webp&to=min&r=${size}`;

  // Cache for future use (limit cache size to prevent memory leaks)
  if (imageUrlCache.size >= maxCacheSize) {
    const firstKey = imageUrlCache.keys().next().value;
    if (firstKey) imageUrlCache.delete(firstKey);
  }
  imageUrlCache.set(cacheKey, optimized);

  return optimized;
};
